import { createContext, ReactNode, useContext, useMemo, useState } from 'react'

import { useGetRegionById } from '@/hooks/queries/use-get-region-by-id'
import { useSearchDemandsByRegion } from '@/hooks/queries/use-search-demands-by-region'
import { Region } from '@/services/get-region-by-id'

type SelectedFields = NonNullable<Region['geojson']>

interface CompareDiagnosesProviderProps {
  children?: ReactNode
  projectId: number
  regionId: number
  fieldIds: string[]
}

function useCompareDiagnosesContext({
  projectId,
  regionId,
  fieldIds,
}: CompareDiagnosesProviderProps) {
  const [baseDiagnosisId, setBaseDiagnosisId] = useState('')
  const [compareDiagnosisId, setCompareDiagnosisId] = useState('')
  const [zoomFieldId, setZoomFieldId] = useState('')

  const { data: allDiagnosesAvailable } = useSearchDemandsByRegion({
    regionId,
    payload: { fields: fieldIds },
  })

  const regionQuery = useGetRegionById(regionId, {
    enabled: !!regionId,
  })

  const selectedFields = useMemo(() => {
    const geojson = regionQuery.data?.geojson

    if (!geojson)
      return {
        type: 'FeatureCollection',
        features: [],
      } as SelectedFields

    const selectedFields: SelectedFields = {
      type: 'FeatureCollection',
      features: geojson.features.filter((feature) =>
        fieldIds.includes(feature.properties.id),
      ),
    }

    return selectedFields
  }, [regionQuery.data?.geojson, fieldIds])

  const allDiagnosesAvailableForCompare = useMemo(() => {
    return allDiagnosesAvailable?.filter(
      (diagnosis) => diagnosis.id !== baseDiagnosisId,
    )
  }, [allDiagnosesAvailable, baseDiagnosisId])

  return {
    projectId,
    regionId,
    fieldIds,
    regionName: regionQuery.data?.name,
    fieldColumnName: regionQuery.data?.col_name,
    selectedFields,
    baseDiagnosisId,
    compareDiagnosisId,
    allDiagnosesAvailable,
    allDiagnosesAvailableForCompare,

    setBaseDiagnosisId: (value: string) => {
      if (value === compareDiagnosisId) {
        setCompareDiagnosisId('')
      }
      setBaseDiagnosisId(value)
    },
    setCompareDiagnosisId,
    zoomFieldId,
    setZoomFieldId,
  }
}

const CompareDiagnosesContext = createContext(
  {} as ReturnType<typeof useCompareDiagnosesContext>,
)

export function CompareDiagnosesProvider({
  children,
  projectId,
  regionId,
  fieldIds,
}: CompareDiagnosesProviderProps) {
  const contextValue = useCompareDiagnosesContext({
    projectId,
    regionId,
    fieldIds,
  })

  return (
    <CompareDiagnosesContext.Provider value={contextValue}>
      {children}
    </CompareDiagnosesContext.Provider>
  )
}

export function useCompareDiagnoses() {
  return useContext(CompareDiagnosesContext)
}
